import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, ChevronRight, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const VendorOnboardingBanner = ({ onStartWizard }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [isCompleted, setIsCompleted] = useState(false);

    const steps = [
        { id: 'welcome', label: 'Welcome', shortLabel: 'Start' },
        { id: 'products', label: 'Add Products', shortLabel: 'Products' },
        { id: 'orders', label: 'Orders', shortLabel: 'Orders' },
        { id: 'profile', label: 'Profile', shortLabel: 'Profile' },
        { id: 'done', label: 'Complete', shortLabel: 'Done' }
    ];

    useEffect(() => {
        // Check if onboarding is completed
        const completed = localStorage.getItem('vendor_onboarding_completed');
        const savedStep = localStorage.getItem('vendor_onboarding_step');
        const dismissed = sessionStorage.getItem('banner_dismissed');

        if (completed === 'true') {
            setIsCompleted(true);
            setIsVisible(false);
        } else if (dismissed === 'true') {
            // Banner was temporarily dismissed this session
            setIsVisible(false);
            setCurrentStep(savedStep ? parseInt(savedStep, 10) : 0);
        } else {
            setIsVisible(true);
            setCurrentStep(savedStep ? parseInt(savedStep, 10) : 0);
        }

        // Listen for localStorage changes (when wizard updates progress)
        const handleStorageChange = () => {
            const completed = localStorage.getItem('vendor_onboarding_completed');
            const savedStep = localStorage.getItem('vendor_onboarding_step');

            if (completed === 'true') {
                setIsCompleted(true);
                setIsVisible(false);
            } else {
                setCurrentStep(savedStep ? parseInt(savedStep, 10) : 0);
            }
        };

        window.addEventListener('storage', handleStorageChange);

        // Also check periodically (for same-tab changes)
        const interval = setInterval(handleStorageChange, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, []);

    const handleDismiss = () => {
        setIsVisible(false);
        // Set a temporary dismissal (will show again on next page load)
        sessionStorage.setItem('banner_dismissed', 'true');
    };

    const handleStartWizard = () => {
        onStartWizard?.();
    };

    const progress = ((currentStep + 1) / steps.length) * 100;

    // Don't render if completed or not visible
    if (isCompleted) return null;

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ y: -100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -100, opacity: 0 }}
                    className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-primary via-primary/95 to-primary/90 text-white shadow-lg"
                    role="banner"
                    aria-label="Onboarding progress banner"
                >
                    <div className="container mx-auto px-4">
                        <div className="flex items-center justify-between py-3 gap-4">
                            {/* Left: Icon & Message */}
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Sparkles className="w-5 h-5" aria-hidden="true" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm sm:text-base">
                                        Complete Your Setup
                                    </p>
                                    <p className="text-xs text-white/80 hidden sm:block">
                                        Step {currentStep + 1} of {steps.length}: {steps[currentStep]?.label}
                                    </p>
                                </div>
                            </div>

                            {/* Center: Progress Bar (Desktop) */}
                            <div className="hidden md:flex items-center gap-2 flex-1 max-w-md" role="progressbar" aria-valuenow={currentStep + 1} aria-valuemin={1} aria-valuemax={steps.length}>
                                {steps.map((step, idx) => (
                                    <div key={step.id} className="flex items-center flex-1">
                                        <div className="flex flex-col items-center flex-1">
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${idx < currentStep
                                                    ? 'bg-green-400 text-green-900'
                                                    : idx === currentStep
                                                        ? 'bg-white text-primary ring-2 ring-white/50'
                                                        : 'bg-white/30 text-white/70'
                                                }`}>
                                                {idx < currentStep ? (
                                                    <CheckCircle className="w-4 h-4" aria-hidden="true" />
                                                ) : (
                                                    idx + 1
                                                )}
                                            </div>
                                            <span className="text-[10px] mt-1 text-white/80">
                                                {step.shortLabel}
                                            </span>
                                        </div>
                                        {idx < steps.length - 1 && (
                                            <div className={`h-0.5 flex-1 mx-1 transition-all ${idx < currentStep ? 'bg-green-400' : 'bg-white/30'
                                                }`} />
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Mobile Progress */}
                            <div className="md:hidden flex items-center gap-2">
                                <div className="w-24 h-2 bg-white/30 rounded-full overflow-hidden">
                                    <motion.div
                                        className="h-full bg-white"
                                        initial={{ width: 0 }}
                                        animate={{ width: `${progress}%` }}
                                        transition={{ duration: 0.3 }}
                                    />
                                </div>
                                <span className="text-xs font-medium whitespace-nowrap">
                                    {currentStep + 1}/{steps.length}
                                </span>
                            </div>

                            {/* Right: Actions */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <Button
                                    onClick={handleStartWizard}
                                    size="sm"
                                    className="bg-white text-primary hover:bg-white/90 gap-1 text-xs sm:text-sm"
                                    aria-label="Continue setup wizard"
                                >
                                    <span className="hidden sm:inline">Continue Setup</span>
                                    <span className="sm:hidden">Continue</span>
                                    <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" aria-hidden="true" />
                                </Button>
                                <button
                                    onClick={handleDismiss}
                                    className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                                    title="Dismiss"
                                    aria-label="Dismiss banner"
                                >
                                    <X className="w-4 h-4" aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Progress Bar */}
                    <div className="h-1 bg-white/20">
                        <motion.div
                            className="h-full bg-white"
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            transition={{ duration: 0.3 }}
                        />
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default VendorOnboardingBanner;
